import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { googleLoginUser } from "../../redux/authSlice";

// The OAuth client id is PUBLIC (it's embedded in every request Google's button makes),
// so it lives in CRA env config, not a secret store. CRA inlines it at build time —
// restart `npm start` after changing .env. With no client id configured the button
// renders nothing (Google sign-in is simply unavailable until it's set up).
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SRC = "https://accounts.google.com/gsi/client";

/**
 * Official "Sign in with Google" button (Google Identity Services), shared by the
 * Login and Register pages — Google sign-in registers on first use, so both pages
 * run the same flow. `text` picks the button wording: "signin_with" | "signup_with".
 *
 * GIS hands the callback a `credential` (a Google-signed ID token); we post it to
 * /autha/google, which verifies it and returns our own JWT — from there the app
 * behaves exactly as after a form login (token stored, socket opened by middleware).
 */
const GoogleSignInButton = ({ text = "signin_with" }) => {
  const buttonRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!CLIENT_ID) return undefined;
    let cancelled = false;

    const init = () => {
      if (cancelled || !window.google?.accounts?.id || !buttonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: async (response) => {
          const result = await dispatch(googleLoginUser(response.credential));
          if (googleLoginUser.fulfilled.match(result)) {
            navigate("/home");
          }
        },
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "outline",
        size: "large",
        text,
        width: 280,
      });
    };

    // Load the GIS script once (shared across mounts/pages), then render into our div.
    if (window.google?.accounts?.id) {
      init();
      return () => { cancelled = true; };
    }
    let script = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", init);
    return () => {
      cancelled = true;
      script.removeEventListener("load", init);
    };
  }, [dispatch, navigate, text]);

  if (!CLIENT_ID) return null;

  return (
    <div className="google-signin">
      <div className="auth-divider">or</div>
      <div className="google-signin-button" ref={buttonRef} />
    </div>
  );
};

export default GoogleSignInButton;
